import { kbImagePreload } from '@theme/kb-image-preload';
import { ThemeEvents } from '@theme/events';
import { crossfadeMedia } from '@theme/kb-view-transition';

/**
 * @typedef {object} KbPdpVariant
 * @property {number} id
 * @property {string} title
 * @property {number} price
 * @property {boolean} available
 * @property {string} [finish]
 */

/**
 * @typedef {object} KbPdpSelection
 * @property {string} kind
 * @property {KbPdpVariant|null} [penVariant]
 * @property {number|string|null} [cartridgeProductId]
 * @property {KbPdpVariant|null} [cartridgeVariant]
 * @property {KbPdpVariant|null} [variant]
 * @property {string|null} [shadeName]
 * @property {string} priceFormatted
 * @property {boolean} available
 * @property {string} [addLabel]
 * @property {string|null} [pendingChoice]
 * @property {string} [shadeStatus]
 * @property {string} [source]
 */

/**
 * @typedef {HTMLElement & {
 *   selection: KbPdpSelection|null,
 *   addToCart: (event?: Event) => void
 * }} KbPdpProductHost
 */

/**
 * @typedef {HTMLElement & { showDialog?: () => void }} KbDialogElement
 */

class KbPdpBuyBox extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      host: 'kb-pdp-product',
      form: '[data-kb-pdp-form]',
      finishDetails: '[data-kb-finish]',
      finishTrigger: '[data-kb-finish-trigger]',
      finishCurrent: '[data-kb-finish-current]',
      finishValue: '[data-kb-finish-value]',
      price: '[data-kb-pdp-price]',
      cartridgeType: '[data-kb-cartridge-type]',
      swatchGrid: '[data-kb-swatch-grid]',
      swatch: '[data-kb-swatch]',
      selectedShade: '[data-kb-selected-shade]',
      nibLabel: '[data-kb-nib-label]',
      shadeStatus: '[data-kb-shade-status]',
      nibTile: '[data-kb-nib-tile]',
      nibImage: '[data-kb-nib-image]',
      nibZoom: '[data-kb-nib-zoom]',
      openDrawer: '[data-kb-open-drawer]',
      shadeDrawer: 'kb-shade-drawer',
      openConfigurator: '[data-kb-open-configurator]',
      addButton: '[data-kb-pdp-add]',
      submitButton: '.kb-pdp-buy-box__add',
      error: '[data-kb-pdp-error]',
      variantIdInput: '[data-kb-variant-id]',
      addToCartComponent: 'add-to-cart-component',
    };
    this.eventNames = {
      optionChange: 'kb:option:change',
      variantChange: 'kb:variant:change',
      lightboxOpen: 'kb:lightbox:open',
      builderOpen: 'kb:builder:open',
    };
    this.selectedClass = 'is-selected';
    /** @type {KbPdpProductHost|null} */
    this.host = null;
    /** @type {string} */
    this.kind = 'accessory';
    /** @type {string|null} */
    this.currentShadeName = null;
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.host = /** @type {KbPdpProductHost|null} */ (this.closest(this.selectors.host));
    this.kind = this.currentKind();
    if (!this.host || !this.host.selection) return;

    const { signal } = this.abortController;
    this.addEventListener('click', this.handleClick, { signal });
    document.addEventListener('click', this.handleDocumentClick, { signal });
    document.addEventListener('keydown', this.handleKeydown, { signal });

    if (this.kind === 'pen') {
      const form = this.querySelector(this.selectors.form);
      if (form) form.addEventListener('submit', this.handlePenSubmit, { signal });
    }

    this.host.addEventListener(this.eventNames.variantChange, this.handleVariantChange, { signal });

    this.host.addEventListener(ThemeEvents.cartError, this.handleCartError, { signal });

    this.paint(this.host.selection);
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  /** @returns {string} */
  currentKind() {
    const form = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.form));
    return (form && form.dataset.kbPdpKind) || 'accessory';
  }

  /** @param {Event} event */
  handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest(this.selectors.submitButton)) this.clearError();

    const openDrawer = target.closest(this.selectors.openDrawer);
    if (openDrawer) {
      event.preventDefault();
      this.openShadeDrawer();
      return;
    }

    const openConfigurator = target.closest(this.selectors.openConfigurator);
    if (openConfigurator) {
      event.preventDefault();
      this.openConfigurator();
      return;
    }

    const nibZoom = target.closest(this.selectors.nibZoom);
    if (nibZoom) {
      event.preventDefault();
      this.dispatchLightboxOpen();
      return;
    }

    const finish = /** @type {HTMLElement|null} */ (target.closest(this.selectors.finishValue));
    if (finish) {
      event.preventDefault();
      this.closeFinish();
      this.dispatchOptionChange('finish', finish.dataset.variantId);
      return;
    }

    const cartridgeType = /** @type {HTMLElement|null} */ (target.closest(this.selectors.cartridgeType));
    if (cartridgeType) {
      event.preventDefault();
      this.dispatchOptionChange('cartridgeType', cartridgeType.dataset.cartridgeId);
      return;
    }

    const swatch = /** @type {HTMLElement|null} */ (target.closest(this.selectors.swatch));
    if (swatch) {
      event.preventDefault();
      if (this.kind === 'pen') {
        this.dispatchOptionChange('shade', swatch.dataset.shadeName, swatch.dataset.variantId);
      } else {
        this.dispatchOptionChange('variant', swatch.dataset.variantId);
      }
    }
  };

  /** @param {Event} event */
  handlePenSubmit = (event) => {
    event.preventDefault();
    if (this.host) this.host.addToCart(event);
  };

  /** @param {Event} event */
  handleVariantChange = (event) => {
    this.clearError();
    this.paint(/** @type {CustomEvent} */ (event).detail);
  };

  /** @param {Event} event */
  handleCartError = (event) => {
    const detail = /** @type {{ detail?: { data?: { message?: string, description?: string } } }} */ (event).detail;
    const data = (detail && detail.data) || {};
    this.showError(data.description || data.message || '');
  };

  /**
   * @param {string} message
   */
  showError(message) {
    const target = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.error));
    if (!target) return;
    target.hidden = false;
    target.textContent = message || target.dataset.errorFallback || '';
  }

  clearError() {
    const target = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.error));
    if (!target || target.hidden) return;
    target.textContent = '';
    target.hidden = true;
  }

  openShadeDrawer() {
    const drawer = /** @type {KbDialogElement|null} */ (document.querySelector(this.selectors.shadeDrawer));
    if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
  }

  openConfigurator() {
    document.dispatchEvent(
      new CustomEvent(this.eventNames.builderOpen, {
        bubbles: true,
        composed: true,
        detail: { source: this.kind },
      })
    );
  }

  dispatchLightboxOpen() {
    this.dispatchEvent(
      new CustomEvent(this.eventNames.lightboxOpen, {
        bubbles: true,
        composed: true,
        detail: { source: 'nib', shadeName: this.currentShadeName, sourceElement: this.querySelector(this.selectors.nibImage) },
      })
    );
  }

  closeFinish() {
    const details = /** @type {HTMLDetailsElement|null} */ (this.querySelector(this.selectors.finishDetails));
    if (details) details.open = false;
  }

  /** @param {Event} event */
  handleDocumentClick = (event) => {
    const details = /** @type {HTMLDetailsElement|null} */ (this.querySelector(this.selectors.finishDetails));
    if (!details || !details.open) return;
    const target = event.target instanceof Node ? event.target : null;
    if (target && !details.contains(target)) details.open = false;
  };

  /** @param {KeyboardEvent} event */
  handleKeydown = (event) => {
    if (event.key === 'Escape') this.closeFinish();
  };

  /**
   * @param {string} optionName
   * @param {string} [value]
   * @param {string} [variantId]
   */
  dispatchOptionChange(optionName, value, variantId) {
    this.dispatchEvent(
      new CustomEvent(this.eventNames.optionChange, {
        bubbles: true,
        composed: true,
        detail: { optionName, value, variantId, source: 'buy-box' },
      })
    );
  }

  /** @param {KbPdpSelection|null} selection */
  paint(selection) {
    if (!selection) return;
    this.paintPrice(selection.priceFormatted);
    if (selection.kind === 'pen') {
      this.paintFinish(selection.penVariant || null);
      this.paintCartridgeType(selection.cartridgeProductId ?? null);
      this.paintNibLabel(selection.cartridgeProductId ?? null);
      this.paintSwatchGrids(selection.cartridgeProductId ?? null, selection.shadeName ?? null);
      this.paintSelectedShade(selection.shadeName ?? null, selection.shadeStatus ?? '');
      this.changeNibTile(selection.cartridgeProductId ?? null, selection.shadeName ?? null, selection.source);
      this.paintPenAvailability(selection);
    } else {
      this.paintSwatchSelection(selection.variant || null);
      this.paintSelectedShade(selection.shadeName ?? null, selection.shadeStatus ?? '');
      this.changeNibTile(null, selection.shadeName ?? null, selection.source);
      this.paintStockForm(selection.variant || null, selection.available);
    }
  }

  /**
   * @param {number|string|null} cartridgeProductId
   * @param {string|null} shadeName
   * @param {string|undefined} source
   */
  changeNibTile(cartridgeProductId, shadeName, source) {
    const update = () => this.paintNibTile(cartridgeProductId, shadeName);
    if (source === 'init') {
      update();
      return;
    }
    crossfadeMedia(this, update);
  }

  /** @param {string} priceFormatted */
  paintPrice(priceFormatted) {
    const price = this.querySelector(this.selectors.price);
    if (price && priceFormatted) price.textContent = priceFormatted;
  }

  /** @param {KbPdpVariant|null} penVariant */
  paintFinish(penVariant) {
    if (!penVariant) return;
    const trigger = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.finishTrigger));
    const current = this.querySelector(this.selectors.finishCurrent);
    if (trigger && penVariant.finish) trigger.dataset.finish = penVariant.finish;
    if (current && penVariant.title) current.textContent = penVariant.title;
    const options = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(this.selectors.finishValue));
    options.forEach((option) => {
      const selected = this.matchesId(option.dataset.variantId, penVariant.id);
      option.classList.toggle(this.selectedClass, selected);
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      this.setCurrent(option, selected);
    });
  }

  /** @param {number|string|null} cartridgeProductId */
  paintCartridgeType(cartridgeProductId) {
    const pills = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(this.selectors.cartridgeType));
    pills.forEach((pill) => {
      const selected = this.matchesId(pill.dataset.cartridgeId, cartridgeProductId);
      pill.classList.toggle(this.selectedClass, selected);
      pill.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  /**
   * The shade label has nothing to sit over until a cartridge is picked, since the
   * shades belong to the cartridge.
   *
   * @param {number|string|null} cartridgeProductId
   */
  paintNibLabel(cartridgeProductId) {
    const label = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.nibLabel));
    if (label) label.hidden = cartridgeProductId == null;
  }

  /**
   * @param {number|string|null} cartridgeProductId
   * @param {string|null} shadeName
   */
  paintSwatchGrids(cartridgeProductId, shadeName) {
    const grids = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(this.selectors.swatchGrid));
    grids.forEach((grid) => {
      const gridCartridgeId = grid.dataset.cartridgeId;
      const isPenGrid = Boolean(gridCartridgeId);
      const visible = !isPenGrid || this.matchesId(gridCartridgeId, cartridgeProductId);
      if (isPenGrid) grid.hidden = !visible;
      if (visible) this.queueNibImages(grid);
      const swatches = /** @type {NodeListOf<HTMLElement>} */ (grid.querySelectorAll(this.selectors.swatch));
      swatches.forEach((swatch) => {
        const selected = visible && swatch.dataset.shadeName === shadeName;
        swatch.classList.toggle(this.selectedClass, selected);
        if (selected) swatch.setAttribute('aria-current', 'true');
        else swatch.removeAttribute('aria-current');
      });
    });
  }

  /**
   * @param {string|null} shadeName
   * @param {string} shadeStatus
   */
  paintSelectedShade(shadeName, shadeStatus) {
    this.currentShadeName = shadeName;
    const target = this.querySelector(this.selectors.selectedShade);
    if (target) target.textContent = shadeName || '';
    const status = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.shadeStatus));
    if (status) {
      status.textContent = shadeStatus;
      status.hidden = shadeStatus === '';
    }
  }

  /**
   * @param {number|string|null} cartridgeProductId
   * @param {string|null} shadeName
   */
  paintNibTile(cartridgeProductId, shadeName) {
    const tile = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.nibTile));
    const image = /** @type {HTMLImageElement|null} */ (this.querySelector(this.selectors.nibImage));
    if (!tile || !image) return;
    const modelImage = this.shadeModelImage(cartridgeProductId, shadeName);
    if (modelImage) {
      image.src = modelImage;
      image.alt = shadeName || '';
      tile.hidden = false;
    } else {
      image.removeAttribute('src');
      tile.hidden = true;
    }
  }

  /**
   * @param {HTMLElement} grid
   */
  queueNibImages(grid) {
    const swatches = /** @type {NodeListOf<HTMLElement>} */ (grid.querySelectorAll(this.selectors.swatch));
    kbImagePreload.add(
      Array.from(swatches).map((swatch) => {
        const url = swatch.dataset.modelImage || '';
        return url ? { url } : null;
      })
    );
  }

  /**
   * @param {number|string|null} cartridgeProductId
   * @param {string|null} shadeName
   * @returns {string}
   */
  shadeModelImage(cartridgeProductId, shadeName) {
    const grids = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(this.selectors.swatchGrid));
    for (const grid of grids) {
      if (grid.dataset.cartridgeId && !this.matchesId(grid.dataset.cartridgeId, cartridgeProductId)) continue;
      const swatches = /** @type {NodeListOf<HTMLElement>} */ (grid.querySelectorAll(this.selectors.swatch));
      for (const swatch of swatches) {
        if (swatch.dataset.shadeName === shadeName) return swatch.dataset.modelImage || '';
      }
    }
    return '';
  }

  /** @param {KbPdpVariant|null} variant */
  paintSwatchSelection(variant) {
    if (!variant) return;
    const grid = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.swatchGrid));
    if (grid) this.queueNibImages(grid);
    const swatches = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll(this.selectors.swatch));
    swatches.forEach((swatch) => {
      const selected = this.matchesId(swatch.dataset.variantId, variant.id);
      swatch.classList.toggle(this.selectedClass, selected);
      this.setCurrent(swatch, selected);
    });
  }

  /**
   * @param {KbPdpVariant|null} variant
   * @param {boolean} available
   */
  paintStockForm(variant, available) {
    const input = /** @type {HTMLInputElement|null} */ (this.querySelector(this.selectors.variantIdInput));
    if (input && variant) input.value = String(variant.id);
    const component = /** @type {{ enable?: () => void, disable?: () => void }|null} */ (
      this.querySelector(this.selectors.addToCartComponent)
    );
    if (!component) return;
    if (available && typeof component.enable === 'function') component.enable();
    if (!available && typeof component.disable === 'function') component.disable();
  }

  /** @param {KbPdpSelection} selection */
  paintPenAvailability(selection) {
    const addButton = /** @type {HTMLButtonElement|null} */ (this.querySelector(this.selectors.addButton));
    if (!addButton) return;
    addButton.disabled = !selection.available;
    const label = this.pendingChoiceLabel(addButton, selection.pendingChoice ?? null) || selection.addLabel;
    if (label) addButton.textContent = label;
  }

  /**
   * @param {HTMLElement} addButton
   * @param {string|null} pendingChoice
   * @returns {string}
   */
  pendingChoiceLabel(addButton, pendingChoice) {
    if (pendingChoice === 'cartridge') return addButton.dataset.labelChooseCartridge || '';
    if (pendingChoice === 'shade') return addButton.dataset.labelChooseShade || '';
    return '';
  }

  /**
   * @param {HTMLElement} element
   * @param {boolean} selected
   */
  setCurrent(element, selected) {
    if (selected) element.setAttribute('aria-current', 'true');
    else element.removeAttribute('aria-current');
  }

  /**
   * @param {number|string|null|undefined} first
   * @param {number|string|null|undefined} second
   * @returns {boolean}
   */
  matchesId(first, second) {
    return first != null && second != null && String(first) === String(second);
  }
}

if (!customElements.get('kb-pdp-buy-box')) {
  customElements.define('kb-pdp-buy-box', KbPdpBuyBox);
}

export { KbPdpBuyBox };
